/* eslint-disable @typescript-eslint/naming-convention */

export interface Patient {
	id: string | null;
	invisalignId?: string | null;
	firstName: string;
	lastName: string;
	dateOfBirth: string;
	gender: string;
	chartNumber: string;
	system: 'iTero' | 'Invisalign' | 'both';
}

export interface PatientFormsContext {
	iTeroPatient: Patient | null;
	invisalignPatient: Patient | null;
	/** Mirrors invisalignPatient state (creating = true, none = false). Needed because services only receive context, not state. */
	invisalignToggle: boolean;
	/** Host-configured default toggle state. Used to restore toggle after clear. */
	defaultInvisalignToggle: boolean;
	cardMatchingPatients: Patient[];
	matchingPatients: MatchCandidate[];
	canCreateNew: boolean;
	searchResults: Patient[];
	pendingIteroPatient: Patient | null;
	pendingInvisalignPatient: Patient | null;
	/** Snapshot of the iTero patient as it was in the DB at selection time. Used for dirty-checking. */
	originalIteroPatient: Patient | null;
	duplicateError: Patient | null;
}

export interface MatchCandidate {
	patient: Patient;
	wouldCreateDuplicate: boolean;
}

export type PatientFormsEvent =
	| { type: 'CARD.CLEAR_CLICKED' }
	| { type: 'CARD.INVISALIGN_TOGGLED' }
	| { type: 'CARD.FORM_UPDATED'; patient: Patient }
	| { type: 'CARD.SEARCH_CLICKED' }
	| { type: 'CARD.REVIEW_MATCHES_CLICKED' }
	| { type: 'CARD.EDITOR_CLICKED' }
	| { type: 'CARD.MATCHES_FOUND'; patients: Patient[] }
	| { type: 'CARD.NO_MATCHES_FOUND' }
	| { type: 'SEARCH.QUERY_ENTERED'; query: string; filter: 'all' | 'iTero' | 'Invisalign' }
	| { type: 'SEARCH.QUERY_CLEARED' }
	| { type: 'SEARCH.PATIENT_SELECTED'; patient: Patient }
	| { type: 'SEARCH.RESULTS'; patients: Patient[] }
	| { type: 'SEARCH.CANCELLED' }
	| { type: 'SEARCH.ERROR_OFFLINE' }
	| { type: 'PATIENT_MATCH.MATCHES_FOUND'; matches: MatchCandidate[]; canCreateNew: boolean }
	| { type: 'PATIENT_MATCH.NO_MATCHES' }
	| { type: 'PATIENT_MATCH.ERROR' }
	| { type: 'LINKING.LINKED'; source: 'iTero' | 'Invisalign'; iTeroPatient: Patient; invisalignPatient: Patient }
	| { type: 'LINKING.CANCELLED' }
	| { type: 'LINKING.CREATE_NEW' }
	// LINKING.CREATE_NEW: user chose to create a new patient in the other system (both systems will have a patient)
	// LINKING.SKIP_INVISALIGN: user chose to use only the iTero patient, skip Invisalign creation/linking (toggle resets)
	| { type: 'LINKING.SKIP_INVISALIGN' }
	| { type: 'EDITOR.SAVED'; patient: Patient; createInvisalign: boolean }
	| { type: 'EDITOR.SAVED_WITHOUT_INVISALIGN'; patient: Patient }
	| { type: 'EDITOR.CANCELLED' }
	| { type: 'EDITOR.NO_DUPLICATE' }
	| { type: 'EDITOR.DUPLICATE_FOUND'; duplicate: Patient }
	| { type: 'EXISTING.PATIENT_LOADED'; patient: Patient }
	| { type: 'CARD.SET_READ_ONLY' }
	| { type: 'CARD.SET_EDITABLE' }
	| { type: 'CARD.SET_DEFAULT_TOGGLE'; on: boolean }
	| { type: 'SYSTEM.WENT_OFFLINE' }
	| { type: 'SYSTEM.WENT_ONLINE' };

export const isPatientModified = (context: PatientFormsContext): boolean => {
	if (!context.originalIteroPatient) {
		return false;
	}

	return JSON.stringify(context.iTeroPatient) !== JSON.stringify(context.originalIteroPatient);
};
